/**
 * T-09 · El único sitio por donde el panel habla con la API.
 *
 * Que sea uno solo es la mitad de la seguridad de este archivo: si cada pantalla
 * llamara a `fetch`, bastaría con que **una** se olvidara de `credentials` para
 * que su petición fuera anónima — y el síntoma sería un 401 confuso, no un
 * agujero visible.
 *
 * ── Lo que NO se guarda, y es deliberado ────────────────────────────────────
 * **Nada.** La sesión vive en una cookie `HttpOnly`, así que el JavaScript de
 * esta página **no puede leerla** — que es exactamente el punto: un XSS en el
 * panel no puede robar la sesión, sólo actuar mientras dure la pestaña.
 *
 * Guardar el token en `localStorage`, que es lo habitual, tira esa protección a
 * la basura: cualquier script que se cuele lo lee y se lo lleva.
 */
import { z } from 'zod';
/** De dónde cuelga la API. Sin valor por omisión en producción — ver más abajo. */
export function baseDeApi() {
    const url = import.meta.env['VITE_API_URL'];
    if (url)
        return url.replace(/\/$/, '');
    // En desarrollo se asume el puerto de siempre. En una compilación de
    // producción **no**: apuntar sin querer a `localhost` desde el panel publicado
    // se ve como «la API no responde» y se busca en el sitio equivocado.
    if (import.meta.env.PROD) {
        throw new Error('Falta VITE_API_URL. Es de dónde cuelga la API — por ejemplo ' +
            'https://api.fialrest.com. Sin ella el panel publicado no sabría a quién ' +
            'preguntar.');
    }
    return 'http://localhost:3000';
}
/**
 * `T-93` · Dónde vive el menú público, **para enseñárselo a una persona**.
 *
 * Es el gemelo en el navegador de `FIALREST_URL_DEL_MENU`, que la API ya usa
 * para redirigir. Existe por [[ADR-031]]: desde que prueba vive en
 * `*.test.fialrest.com`, el host dejó de ser una constante del producto y pasó a
 * ser un dato del entorno.
 *
 * ── Por qué devuelve el host pelado y no la URL ─────────────────────────────
 * Porque los seis sitios que lo usan **lo pintan, no lo navegan**: el panel lo
 * enseña para copiar y la consola lo pega delante del identificador mientras
 * Enrique lo teclea. `https://` delante de un campo de texto es ruido para quien
 * va a dictar la dirección por teléfono, que es el caso real.
 *
 * La variable sí guarda la URL entera, por dos razones: es la forma que ya
 * tienen `VITE_API_URL` y `FIALREST_URL_DEL_MENU` —una convención a medias es
 * peor que cualquiera de las dos enteras—, y `new URL()` valida de balde.
 *
 * ── El respaldo es el valor de antes, y sólo en desarrollo ──────────────────
 * Igual que `baseDeApi()`: en una compilación de producción **no hay respaldo**.
 * Un panel publicado que enseñe el host del otro entorno es exactamente el
 * defecto que esta función existe para impedir — y es peor que no enseñar nada,
 * porque la dirección **sí abre** y es la cafetería de otro entorno.
 */
export function hostDelMenu() {
    const crudo = import.meta.env['VITE_URL_DEL_MENU'];
    if (crudo !== undefined && crudo !== '') {
        let url;
        try {
            url = new URL(crudo);
        }
        catch {
            //  Se avisa en los dos entornos y no sólo en producción: una variable mal
            //  escrita en desarrollo que cayera al respaldo en silencio se descubriría
            //  publicada, que es el peor sitio para descubrirla.
            throw new Error(`VITE_URL_DEL_MENU no es una URL válida: ${crudo}. Se espera algo como ` +
                'https://menu.fialrest.com.');
        }
        //  `host` y no `hostname`: si alguien pone un puerto, es porque lo necesita.
        return url.host;
    }
    if (import.meta.env.PROD) {
        throw new Error('Falta VITE_URL_DEL_MENU. Es la dirección que el panel le enseña al dueño ' +
            'para copiar y la que la consola pega delante del identificador — por ' +
            'ejemplo https://menu.fialrest.com. Sin ella, cada entorno enseñaría la ' +
            'dirección del otro.');
    }
    //  En desarrollo, lo que estaba cocido hasta `T-93`. No navega nada: es texto.
    return 'menu.fialrest.com';
}
/** Lo que la API devuelve cuando algo va mal. Se valida: es entrada, como todo. */
const esquemaError = z.object({
    statusCode: z.number().optional(),
    error: z.string().optional(),
    mensaje: z.string().optional(),
    motivo: z.string().optional(),
    modulo: z.string().optional(),
    reintentarEnSegundos: z.number().optional(),
}).loose();
export class ErrorDeApi extends Error {
    estado;
    cuerpo;
    constructor(estado, cuerpo) {
        // El mensaje que la API manda en español ya está pensado para leerse. Si no
        // vino uno, se pone algo neutro: nunca el cuerpo crudo, que puede traer
        // detalle interno.
        super(cuerpo.mensaje ?? 'No se pudo completar la operación.');
        this.estado = estado;
        this.cuerpo = cuerpo;
        this.name = 'ErrorDeApi';
    }
    /** Sin sesión, o con una que ya no vale. */
    get esSinSesion() { return this.estado === 401; }
    /** Módulo no contratado, suspendido o cerrado. */
    get esModulo() { return this.estado === 402; }
}
/**
 * Pide algo a la API y **valida la respuesta contra un esquema**.
 *
 * La respuesta del servidor es entrada como cualquier otra: un despliegue a
 * medias, un proxy que devuelve HTML, o una versión vieja de la API pueden
 * mandar algo que no encaja. Sin esquema eso se convierte en un `undefined`
 * dentro de un componente, tres pantallas después.
 */
export async function pedir(ruta, esquema, opciones = {}) {
    const respuesta = await fetch(`${baseDeApi()}${ruta}`, {
        method: opciones.metodo ?? 'GET',
        // **Sin esto la cookie no viaja** y todo responde 401. Es la línea que
        // justifica que este archivo exista.
        credentials: 'include',
        headers: opciones.cuerpo === undefined ? {} : { 'Content-Type': 'application/json' },
        ...(opciones.cuerpo === undefined ? {} : { body: JSON.stringify(opciones.cuerpo) }),
        ...(opciones.señal ? { signal: opciones.señal } : {}),
    });
    if (!respuesta.ok) {
        const crudo = await respuesta.json().catch(() => ({}));
        const cuerpo = esquemaError.safeParse(crudo);
        throw new ErrorDeApi(respuesta.status, cuerpo.success ? cuerpo.data : {});
    }
    if (respuesta.status === 204)
        return undefined;
    const crudo = await respuesta.json();
    const salida = esquema.safeParse(crudo);
    if (!salida.success) {
        throw new ErrorDeApi(respuesta.status, {
            mensaje: 'La respuesta del servidor no tiene la forma esperada. ' +
                `Puede que haya un despliegue a medias. ${dondeFalla(salida.error)}`,
        });
    }
    return salida.data;
}
/**
 * **Qué campo no encajó**, dicho en la pantalla.
 *
 * > [!danger] Sin esto, un desajuste de contrato no se puede diagnosticar
 * > El mensaje anterior decía «puede que haya un despliegue a medias; vuelve a
 * > intentarlo» y tiraba el error de `zod` a la basura. Reintentar no arregla un
 * > contrato roto, así que lo único que quedaba era una pantalla que fallaba
 * > siempre y no decía por qué. Pasó de verdad, con el catálogo de extras, y
 * > costó una tarde mirando el sitio equivocado — la API, que contestaba `200`.
 *
 * ── Los NOMBRES de los campos, nunca sus valores ──────────────────────────
 * Lo que sale es la ruta —`usos.0.productos`— y qué se esperaba, no lo que
 * llegó. Un valor puede ser un teléfono, un nombre o el precio de otra
 * cafetería, y esto se pinta en una pantalla que alguien puede fotografiar.
 * `ADR-012` para lo que no debe salir de su sitio.
 *
 * Y sólo el primero: tres rutas encadenadas en una franja de error no se leen, y
 * cuando un contrato se desajusta el primero suele ser toda la historia.
 */
function dondeFalla(error) {
    const fallo = error.issues[0];
    if (!fallo)
        return 'El servidor no dijo qué falta.';
    const donde = fallo.path.length === 0 ? 'la raíz de la respuesta' : `«${fallo.path.join('.')}»`;
    return `Falla en ${donde}: ${fallo.message}.`;
}
//# sourceMappingURL=cliente.js.map